import { graphql, useMutation, usePaginationFragment } from "react-relay";
import { TodoEditTimeline_todo$key } from "./__generated__/TodoEditTimeline_todo.graphql";
import { AHIcon, AHText, Box, Circle, Input } from "arkh";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useCallback, useRef, useState } from "react";
import {
  TodoTimelineItem,
  TodoTimelineVerticalLine,
  TodoTimelineCreatedEvent,
} from "widgets";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { TodoEditTimelineCommentMutation } from "./__generated__/TodoEditTimelineCommentMutation.graphql";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  TextInput,
  View,
} from "react-native";
import Animated from "react-native-reanimated";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useAlert } from "@/src/common/Alert";
import { TodoEditTimelineDeleteCommentMutation } from "./__generated__/TodoEditTimelineDeleteCommentMutation.graphql";
import { MoreButton } from "@/src/common/MoreButton";
import { BottomSheetTextInput } from "@gorhom/bottom-sheet";

export const TodoEditNewMainTimeline = React.memo(TodoEditNewMainTimelineC);
function TodoEditNewMainTimelineC(props: {
  todo: TodoEditTimeline_todo$key;

  onRequestScrollToBottom: () => void;
  inBottomSheet: boolean;
}) {
  const { data } = usePaginationFragment(
    graphql`
      fragment TodoEditTimeline_todo on Todo
      @refetchable(queryName: "TodoEditTimeline_todoRefetchQuery")
      @argumentDefinitions(
        last: { type: "Int", defaultValue: 100 }
        before: { type: "String", defaultValue: null }
      ) {
        ...TodoTimelineUpdateEventCreated_todo
        timeline(last: $last, before: $before)
          @connection(key: "TodoEditTimeline__timeline") {
          __id
          edges {
            node {
              ... on TodoComment {
                canDelete
                id
              }
              ...TodoTimeline_item
            }
          }
        }
      }
    `,
    props.todo
  );

  if (!data.timeline) {
    return null;
  }

  return (
    <Box
      color="surface-background-light"
      spaceVertical="small"
      spaceTop="medium"
      gap="small"
      style={{ maxWidth: 500, width: "100%", alignSelf: "center" }}
    >
      <AHText type="subtitle" spaceHorizontal="small" color="text-secondary">
        <FormattedMessage
          description="Todo Edit Timeline Title"
          defaultMessage="Activity"
        />
      </AHText>

      <Box gap="small">
        <TodoTimelineVerticalLine />
        <TodoTimelineCreatedEvent todo={data} />
        {data.timeline?.edges.map((edge, i) =>
          edge.node ? (
            <TodoTimelineItem
              key={i + ""}
              item={edge.node}
              commentHeaderRight={
                edge.node.canDelete && edge.node.id ? (
                  <CommentMoreButton
                    canDelete={edge.node.canDelete}
                    commentId={edge.node.id}
                  />
                ) : null
              }
            />
          ) : null
        )}
        <NewCommentItem
          todoId={data.id}
          inBottomSheet={props.inBottomSheet}
          connections={[data.timeline.__id]}
          onRequestScrollToBottom={props.onRequestScrollToBottom}
        />
      </Box>
    </Box>
  );
}

function CommentMoreButton(props: { canDelete: boolean; commentId: string }) {
  const intl_tokens = useIntlTokens();

  const [deleteCommentMutation] =
    useMutation<TodoEditTimelineDeleteCommentMutation>(graphql`
      mutation TodoEditTimelineDeleteCommentMutation($commentId: ID!) {
        todoComment(id: $commentId) {
          delete {
            id @deleteRecord
          }
        }
      }
    `);
  const intl = useIntl();
  const Alert = useAlert();

  const title = intl.formatMessage({
    defaultMessage: "Delete Comment",
    description: "Todo Edit Timeline Comment Delete Title",
  });
  const body = intl.formatMessage({
    defaultMessage: "Are you sure you want to delete this comment?",
    description: "Todo Edit Timeline Comment Delete Body",
  });
  const requestDelete = useCallback(() => {
    Alert.alert(title, body, [
      { text: intl_tokens.Cancel, style: "cancel" },
      {
        text: intl_tokens.Delete,
        style: "destructive",
        onPress: () => {
          deleteCommentMutation({
            variables: {
              commentId: props.commentId,
            },
            onCompleted: () => {},
          });
        },
      },
    ]);
  }, [
    Alert,
    title,
    body,
    intl_tokens.Cancel,
    intl_tokens.Delete,
    deleteCommentMutation,
    props.commentId,
  ]);

  if (!props.canDelete) {
    return null;
  }

  return (
    <View style={{ flex: 1 }}>
      <MoreButton
        items={[
          {
            label: intl_tokens.Delete,
            onPress() {
              requestDelete();
            },
            color: "negative",
            icon: "trash",
            type: "button",
          },
        ]}
      >
        {(onOpen) => (
          <Pressable
            hitSlop={20}
            onPress={onOpen}
            style={({ pressed }) => ({
              opacity: pressed ? 0.7 : 1,
              alignSelf: "flex-end",
            })}
          >
            <AHIcon spaceRight="small" type="more" color="text-secondary" />
          </Pressable>
        )}
      </MoreButton>
    </View>
  );
}

function NewCommentItem(props: {
  todoId: string;
  onRequestScrollToBottom: () => void;

  connections: string[];
  inBottomSheet: boolean;
}) {
  const [comment, setComment] = useState("");
  const { currentEmployeeId } = usePersistedEmployeeId();
  const [createComment, loading] = useMutation<TodoEditTimelineCommentMutation>(
    graphql`
      mutation TodoEditTimelineCommentMutation(
        $comment: String!
        $todoId: ID!
        $employeeId: ID
        $connections: [ID!]!
      ) {
        todo(todoId: $todoId) {
          comment(input: { comment: $comment }, employeeId: $employeeId) {
            node
              @appendNode(
                connections: $connections
                edgeTypeName: "TodoTimelineEdge"
              ) {
              ...TodoTimeline_item
              id
              canDelete
            }
          }
        }
      }
    `
  );
  const ref = useRef<TextInput>(null);

  const intl = useIntl();
  const placeholder = intl.formatMessage({
    defaultMessage: "Leave a comment...",
    description: "Todo Edit Timeline Comment Placeholder",
  });

  const onRequestScrollToBottomProp = props.onRequestScrollToBottom;
  const requestScrollToBottom = useCallback(() => {
    onRequestScrollToBottomProp();
  }, [onRequestScrollToBottomProp]);

  const onSubmit = useCallback(() => {
    createComment({
      variables: {
        comment,
        todoId: props.todoId,
        employeeId: currentEmployeeId,
        connections: props.connections,
      },
      onCompleted: () => {
        setComment("");
        ref.current?.blur();

        requestScrollToBottom();
      },
    });
  }, [comment, createComment, currentEmployeeId, props, requestScrollToBottom]);

  const disabled = loading || comment.length === 0;

  return (
    <>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <View style={{ flex: 1 }}>
          <Input
            disabled={loading}
            placeholder={placeholder}
            value={comment}
            onChangeText={setComment}
            onSubmitEditing={Platform.OS === "web" ? onSubmit : undefined}
            submitBehavior={Platform.OS === "web" ? "submit" : "newline"}
            onFocus={() => {
              requestScrollToBottom();
            }}
            autoComplete="off"
            autoCapitalize="sentences"
            autoCorrect={true}
            style={{ paddingRight: 120 }}
            multiline={true}
            inputComponent={
              props.inBottomSheet ? BottomSheetTextInput : TextInput
            }
          />
        </View>

        <Box
          style={{ width: 50, position: "absolute", right: 0 }}
          spaceRight="small"
        >
          <Animated.View
            style={{
              opacity: comment.length === 0 ? 0 : 1,
              transition: "opacity 0.2s ease-in-out",
            }}
          >
            <Pressable
              onPress={onSubmit}
              disabled={disabled}
              style={({ pressed }) => ({
                opacity: pressed ? 0.5 : 1,
              })}
            >
              <Circle size="medium" color="primary">
                {loading ? (
                  <ActivityIndicator color="#FFF" size="small" />
                ) : (
                  <AHIcon type="send" color="text-inverted" />
                )}
              </Circle>
            </Pressable>
          </Animated.View>
        </Box>
      </View>
    </>
  );
}
